import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Search, User as UserIcon, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ParticipantTools } from "../components/app/admin/ParticipantTools";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "2-digit", year: "numeric" }) : "—");

export default function AdminParticipants() {
  const [participants, setParticipants] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/admin/participants", { params: { q: search.trim(), _: Date.now() } })
      .then((r) => setParticipants(r.data))
      .catch(() => toast.error("Failed to load participants"))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 min-h-[520px]" data-testid="admin-participants">
      <aside className="flex flex-col border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden lg:h-[calc(100vh-260px)]">
        <div className="p-4 border-b border-zinc-900 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300/80">participants</div>
            <button data-testid="participants-refresh-btn" onClick={load} className="text-zinc-500 hover:text-emerald-300 transition-colors" title="Refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input data-testid="participants-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or business..."
              className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-xs text-white outline-none focus:border-emerald-500/50" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto max-h-[60vh] lg:max-h-none">
          {loading && <div className="text-xs text-zinc-500 p-6 text-center">Loading...</div>}
          {!loading && participants.length === 0 && <div className="text-xs text-zinc-500 p-6 text-center">No participants match.</div>}
          {!loading && participants.map((p) => (
            <button key={p.id} data-testid={`participant-row-${p.id}`} onClick={() => setSelected(p)}
              className={`w-full text-left px-4 py-3 border-b border-zinc-900 transition-colors ${selected?.id === p.id ? "bg-emerald-500/10 border-l-2 border-l-emerald-400" : "hover:bg-zinc-900/60"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-white font-medium truncate">{p.name || p.email}</span>
                <span className="text-[10px] text-zinc-600 shrink-0">{fmtDate(p.last_activity)}</span>
              </div>
              <div className="flex items-center justify-between mt-1 text-[11px] text-zinc-400">
                <span className="truncate inline-flex items-center gap-1.5"><UserIcon className="w-3 h-3 text-zinc-500" />{p.email}</span>
                <span className="shrink-0 text-zinc-500">{p.tool_count} GPT{p.tool_count === 1 ? "" : "s"} · {p.session_count} chat{p.session_count === 1 ? "" : "s"}</span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      <section className="border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden min-h-0 lg:h-[calc(100vh-260px)] flex flex-col" data-testid="participant-detail-pane">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4"><Users className="w-6 h-6 text-emerald-300" /></div>
            <h3 className="font-display text-2xl tracking-tighter font-black text-white">Select a participant</h3>
            <p className="text-zinc-500 text-sm mt-2 max-w-sm">See every GPT they have used and download their conversations with each one as a PDF.</p>
          </div>
        ) : (
          <ParticipantTools key={selected.id} participant={selected} />
        )}
      </section>
    </div>
  );
}


